import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Mail, Lock, BarChart3, Package, Users, Shield, KeyRound, Check, X as XIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorUtils';
import { forgotPassword, resetPassword, changePassword } from '../services/api';

const COLORS = {
  navyBlue: '#1e3a5f',
  darkSlate: '#0f172a',
  orange: '#F37322',
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, clearMustChangePassword } = useAuth();
  const navigate = useNavigate();

  // Forced password change modal state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [cpNewPassword, setCpNewPassword] = useState('');
  const [cpConfirmPassword, setCpConfirmPassword] = useState('');
  const [showCpPassword, setShowCpPassword] = useState(false);
  const [cpLoading, setCpLoading] = useState(false);

  // Recovery state
  const [recoveryStep, setRecoveryStep] = useState(null); // null | 'email' | 'code' | 'password'
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryCode, setRecoveryCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const codeInputRefs = useRef([]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setInterval(() => setResendCountdown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [resendCountdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await login({ email, password });
      const user = response.data.user;

      // If user must change password, show modal instead of navigating
      if (response.data.mustChangePassword) {
        setShowChangePassword(true);
        setLoading(false);
        return;
      }

      toast.success('¡Bienvenido!');

      if (user.role === 'superadmin') {
        navigate('/superadmin');
      } else if (!user.email_verified) {
        navigate('/verify-email');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(getErrorMessage(error, 'Error al iniciar sesión'));
    } finally {
      setLoading(false);
    }
  };

  // --- Recovery handlers ---
  const handleSendResetCode = async (e) => {
    e.preventDefault();
    if (!recoveryEmail.trim()) return;
    setLoading(true);
    try {
      await forgotPassword(recoveryEmail.trim());
      toast.success('Si el correo está registrado, recibirás un código');
      setRecoveryStep('code');
      setResendCountdown(60);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al enviar código'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCountdown > 0) return;
    try {
      await forgotPassword(recoveryEmail.trim());
      setResendCountdown(60);
      toast.success('Código reenviado');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al reenviar'));
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newCode = [...recoveryCode];
      digits.forEach((d, i) => { if (index + i < 6) newCode[index + i] = d; });
      setRecoveryCode(newCode);
      const nextIndex = Math.min(index + digits.length, 5);
      codeInputRefs.current[nextIndex]?.focus();
      if (newCode.every(d => d !== '')) handleCodeComplete(newCode.join(''));
      return;
    }
    const digit = value.replace(/\D/g, '');
    const newCode = [...recoveryCode];
    newCode[index] = digit;
    setRecoveryCode(newCode);
    if (digit && index < 5) codeInputRefs.current[index + 1]?.focus();
    if (digit && newCode.every(d => d !== '')) handleCodeComplete(newCode.join(''));
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !recoveryCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...recoveryCode];
      pasted.split('').forEach((d, i) => { newCode[i] = d; });
      setRecoveryCode(newCode);
      codeInputRefs.current[Math.min(pasted.length, 5)]?.focus();
      if (newCode.every(d => d !== '')) handleCodeComplete(newCode.join(''));
    }
  };

  const handleCodeComplete = (codeStr) => {
    if (codeStr.length === 6) {
      setRecoveryStep('password');
    }
  };

  const handleVerifyAndContinue = () => {
    const codeStr = recoveryCode.join('');
    if (codeStr.length !== 6) {
      toast.error('Ingresa el código completo de 6 dígitos');
      return;
    }
    setRecoveryStep('password');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      toast.error('La contraseña debe tener al menos una letra mayúscula');
      return;
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(newPassword)) {
      toast.error('La contraseña debe tener al menos un signo especial');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    setLoading(true);
    try {
      const codeStr = recoveryCode.join('');
      await resetPassword(recoveryEmail.trim(), codeStr, newPassword);
      toast.success('Contraseña actualizada exitosamente');
      // Reset recovery state and go back to login
      setRecoveryStep(null);
      setRecoveryEmail('');
      setRecoveryCode(['', '', '', '', '', '']);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const msg = getErrorMessage(err, 'Error al cambiar contraseña');
      toast.error(msg);
      if (msg.includes('expirado') || msg.includes('incorrecto')) {
        setRecoveryStep('code');
        setRecoveryCode(['', '', '', '', '', '']);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetRecovery = () => {
    setRecoveryStep(null);
    setRecoveryEmail('');
    setRecoveryCode(['', '', '', '', '', '']);
    setNewPassword('');
    setConfirmPassword('');
    setLoading(false);
  };

  // --- Forced password change handler ---
  const handleForceChangePassword = async (e) => {
    e.preventDefault();
    if (cpNewPassword.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (!/[A-Z]/.test(cpNewPassword)) {
      toast.error('La contraseña debe tener al menos una letra mayúscula');
      return;
    }
    if (!/[a-z]/.test(cpNewPassword)) {
      toast.error('La contraseña debe tener al menos una letra minúscula');
      return;
    }
    if (!/[0-9]/.test(cpNewPassword)) {
      toast.error('La contraseña debe tener al menos un número');
      return;
    }
    if (cpNewPassword !== cpConfirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    setCpLoading(true);
    try {
      await changePassword(cpNewPassword);
      clearMustChangePassword();
      toast.success('Contraseña actualizada exitosamente');
      setShowChangePassword(false);

      // Navigate based on user role
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (storedUser.role === 'superadmin') {
        navigate('/superadmin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al cambiar contraseña'));
    } finally {
      setCpLoading(false);
    }
  };

  const features = [
    { icon: BarChart3, title: 'Reportes en tiempo real', desc: 'Ventas, inventario y más' },
    { icon: Package, title: 'Gestión de productos', desc: 'Catálogo completo con imágenes' },
    { icon: Users, title: 'Control de sucursales', desc: 'Cajeros, repartidores y más' },
    { icon: Shield, title: 'Punto de venta', desc: 'POS profesional integrado' },
  ];

  // --- Recovery UI ---
  const renderRecovery = () => {
    if (recoveryStep === 'email') {
      return (
        <div className="max-w-sm mx-auto">
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: COLORS.navyBlue + '10' }}>
              <KeyRound className="w-8 h-8" style={{ color: COLORS.navyBlue }} />
            </div>
            <h2 className="text-2xl font-bold" style={{ color: COLORS.darkSlate }}>Recuperar contraseña</h2>
            <p className="mt-2 text-sm text-slate-500 text-center">Ingresa tu correo y te enviaremos un código de verificación</p>
          </div>
          <form onSubmit={handleSendResetCode} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Correo electrónico</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  required
                  value={recoveryEmail}
                  onChange={(e) => setRecoveryEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow sm:text-sm"
                  style={{ '--tw-ring-color': COLORS.navyBlue + '40' }}
                  placeholder="tu@email.com"
                  autoFocus
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 text-sm font-semibold rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:opacity-90"
              style={{ backgroundColor: COLORS.orange }}
            >
              {loading ? 'Enviando...' : 'Enviar código'}
            </button>
          </form>
          <button onClick={resetRecovery} className="w-full mt-4 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors">
            Volver al login
          </button>
        </div>
      );
    }

    if (recoveryStep === 'code') {
      return (
        <div className="max-w-sm mx-auto">
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: COLORS.navyBlue + '10' }}>
              <Mail className="w-8 h-8" style={{ color: COLORS.navyBlue }} />
            </div>
            <h2 className="text-2xl font-bold" style={{ color: COLORS.darkSlate }}>Ingresa el código</h2>
            <p className="mt-2 text-sm text-slate-500 text-center">Enviamos un código de 6 dígitos a</p>
            <p className="mt-1 text-sm font-medium" style={{ color: COLORS.navyBlue }}>{recoveryEmail}</p>
          </div>

          <div className="flex justify-center gap-2 mb-6" onPaste={handleCodePaste}>
            {recoveryCode.map((digit, index) => (
              <input
                key={index}
                ref={el => codeInputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleCodeKeyDown(index, e)}
                className="w-12 h-14 text-center text-xl font-bold border-2 border-slate-200 rounded-xl focus:outline-none focus:border-transparent focus:ring-2 transition-all"
                style={{ '--tw-ring-color': COLORS.navyBlue + '60', color: COLORS.darkSlate }}
                autoFocus={index === 0}
              />
            ))}
          </div>

          <button
            onClick={handleVerifyAndContinue}
            disabled={recoveryCode.some(d => d === '')}
            className="w-full py-3 text-sm font-semibold rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:opacity-90"
            style={{ backgroundColor: COLORS.orange }}
          >
            Continuar
          </button>

          <div className="text-center mt-5">
            <p className="text-sm text-slate-500 mb-2">¿No recibiste el código?</p>
            <button
              onClick={handleResendCode}
              disabled={resendCountdown > 0}
              className="text-sm font-semibold disabled:opacity-50 transition-colors"
              style={{ color: resendCountdown > 0 ? '#94a3b8' : COLORS.orange }}
            >
              {resendCountdown > 0 ? `Reenviar en ${resendCountdown}s` : 'Reenviar código'}
            </button>
          </div>
          <button onClick={resetRecovery} className="w-full mt-4 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors">
            Volver al login
          </button>
        </div>
      );
    }

    if (recoveryStep === 'password') {
      return (
        <div className="max-w-sm mx-auto">
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: COLORS.navyBlue + '10' }}>
              <Lock className="w-8 h-8" style={{ color: COLORS.navyBlue }} />
            </div>
            <h2 className="text-2xl font-bold" style={{ color: COLORS.darkSlate }}>Nueva contraseña</h2>
            <p className="mt-2 text-sm text-slate-500 text-center">Ingresa tu nueva contraseña</p>
          </div>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Nueva contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow sm:text-sm"
                  style={{ '--tw-ring-color': COLORS.navyBlue + '40' }}
                  placeholder="Mínimo 6 caracteres"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {newPassword.length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                  <span className={`flex items-center gap-1 text-xs ${newPassword.length >= 8 ? 'text-green-600' : 'text-slate-400'}`}>
                    {newPassword.length >= 8 ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                    8+ caracteres
                  </span>
                  <span className={`flex items-center gap-1 text-xs ${/[A-Z]/.test(newPassword) ? 'text-green-600' : 'text-slate-400'}`}>
                    {/[A-Z]/.test(newPassword) ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                    1 mayúscula
                  </span>
                  <span className={`flex items-center gap-1 text-xs ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(newPassword) ? 'text-green-600' : 'text-slate-400'}`}>
                    {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(newPassword) ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                    1 signo especial
                  </span>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirmar contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow sm:text-sm"
                  style={{ '--tw-ring-color': COLORS.navyBlue + '40' }}
                  placeholder="Repite tu contraseña"
                />
              </div>
              {confirmPassword.length > 0 && newPassword !== confirmPassword && (
                <p className="text-xs text-red-500 mt-1">Las contraseñas no coinciden</p>
              )}
            </div>
            <button
              type="submit"
              disabled={loading || newPassword.length < 8 || !/[A-Z]/.test(newPassword) || !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(newPassword) || newPassword !== confirmPassword}
              className="w-full py-3 text-sm font-semibold rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:opacity-90"
              style={{ backgroundColor: COLORS.orange }}
            >
              {loading ? 'Cambiando...' : 'Cambiar contraseña'}
            </button>
          </form>
          <button onClick={resetRecovery} className="w-full mt-4 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors">
            Volver al login
          </button>
        </div>
      );
    }

    return null;
  };

  // --- Forced password change modal ---
  const renderChangePasswordModal = () => {
    if (!showChangePassword) return null;
    const cpValid = cpNewPassword.length >= 8 && /[A-Z]/.test(cpNewPassword) && /[a-z]/.test(cpNewPassword) && /[0-9]/.test(cpNewPassword) && cpNewPassword === cpConfirmPassword;
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
          <div className="flex flex-col items-center mb-6">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: COLORS.navyBlue + '10' }}>
              <Lock className="w-7 h-7" style={{ color: COLORS.navyBlue }} />
            </div>
            <h2 className="text-xl font-bold" style={{ color: COLORS.darkSlate }}>Cambio de contraseña obligatorio</h2>
            <p className="mt-2 text-sm text-slate-500 text-center">
              Tu cuenta fue creada con una contraseña temporal. Por seguridad, debes establecer una nueva contraseña.
            </p>
          </div>
          <form onSubmit={handleForceChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Nueva contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showCpPassword ? 'text' : 'password'}
                  required
                  value={cpNewPassword}
                  onChange={(e) => setCpNewPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow sm:text-sm"
                  style={{ '--tw-ring-color': COLORS.navyBlue + '40' }}
                  placeholder="Mínimo 8 caracteres"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowCpPassword(!showCpPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showCpPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {cpNewPassword.length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                  <span className={`flex items-center gap-1 text-xs ${cpNewPassword.length >= 8 ? 'text-green-600' : 'text-slate-400'}`}>
                    {cpNewPassword.length >= 8 ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                    8+ caracteres
                  </span>
                  <span className={`flex items-center gap-1 text-xs ${/[A-Z]/.test(cpNewPassword) ? 'text-green-600' : 'text-slate-400'}`}>
                    {/[A-Z]/.test(cpNewPassword) ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                    1 mayúscula
                  </span>
                  <span className={`flex items-center gap-1 text-xs ${/[a-z]/.test(cpNewPassword) ? 'text-green-600' : 'text-slate-400'}`}>
                    {/[a-z]/.test(cpNewPassword) ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                    1 minúscula
                  </span>
                  <span className={`flex items-center gap-1 text-xs ${/[0-9]/.test(cpNewPassword) ? 'text-green-600' : 'text-slate-400'}`}>
                    {/[0-9]/.test(cpNewPassword) ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                    1 número
                  </span>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirmar contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showCpPassword ? 'text' : 'password'}
                  required
                  value={cpConfirmPassword}
                  onChange={(e) => setCpConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow sm:text-sm"
                  style={{ '--tw-ring-color': COLORS.navyBlue + '40' }}
                  placeholder="Repite tu contraseña"
                />
              </div>
              {cpConfirmPassword.length > 0 && cpNewPassword !== cpConfirmPassword && (
                <p className="text-xs text-red-500 mt-1">Las contraseñas no coinciden</p>
              )}
            </div>
            <button
              type="submit"
              disabled={cpLoading || !cpValid}
              className="w-full py-3 text-sm font-semibold rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:opacity-90"
              style={{ backgroundColor: COLORS.orange }}
            >
              {cpLoading ? 'Guardando...' : 'Establecer nueva contraseña'}
            </button>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {renderChangePasswordModal()}
      <div className="flex-1 flex flex-col items-center justify-center py-8 px-4">
        <div className="w-full max-w-4xl mb-3">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Volver al marketplace
          </Link>
        </div>
        <div className="w-full max-w-4xl flex rounded-2xl overflow-hidden shadow-sm bg-white">
          {/* Left panel - features (desktop only) */}
          <div className="hidden lg:flex flex-col justify-center w-[380px] p-10" style={{ backgroundColor: COLORS.navyBlue }}>
            <h2 className="text-2xl font-bold text-white mb-1">
              Panel de Negocios
            </h2>
            <p className="text-blue-200 text-sm mb-8">
              Administra tu negocio desde un solo lugar
            </p>
            <div className="space-y-5">
              {features.map((f, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <f.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm">{f.title}</h3>
                    <p className="text-blue-200 text-xs mt-0.5">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right panel - form */}
          <div className="flex-1 p-6 sm:p-10">
            {recoveryStep ? renderRecovery() : (
            <div className="max-w-sm mx-auto">
              <div className="flex flex-col items-center mb-8">
                <img src="/logoenlocal.png" alt="Todo en Local" className="h-16 w-auto mb-4" />
                <h2 className="text-2xl font-bold" style={{ color: COLORS.darkSlate }}>
                  Todo En Local <span style={{ color: COLORS.navyBlue }}>Negocios</span>
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Inicia sesión en tu cuenta
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Correo electrónico
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      data-testid="login-email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow sm:text-sm"
                      style={{ '--tw-ring-color': COLORS.navyBlue + '40' }}
                      placeholder="tu@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Contraseña
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      data-testid="login-password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-10 py-3 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow sm:text-sm"
                      style={{ '--tw-ring-color': COLORS.navyBlue + '40' }}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="login-submit-button"
                  disabled={loading}
                  className="w-full py-3 text-sm font-semibold rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:opacity-90"
                  style={{ backgroundColor: COLORS.orange }}
                >
                  {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setRecoveryStep('email')}
                    className="text-sm font-medium hover:underline transition-colors"
                    style={{ color: COLORS.navyBlue }}
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>

                <div className="text-center pt-1">
                  <p className="text-sm text-slate-500">
                    ¿No tienes cuenta?{' '}
                    <Link to="/register" className="font-semibold" style={{ color: COLORS.orange }}>
                      Regístrate aquí
                    </Link>
                  </p>
                </div>
              </form>

              {/* Customer login link */}
              <div className="mt-6 pt-5 border-t border-slate-100 text-center">
                <p className="text-sm text-slate-500">
                  ¿Buscas comprar productos?{' '}
                  <Link to="/auth" className="font-semibold" style={{ color: COLORS.navyBlue }}>
                    Ingresa como cliente
                  </Link>
                </p>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
